import React, { useEffect, useState } from "react";
import Column from "@components/column";
import Table from "@components/table";
import Role from "@apis/role";
import { useSelector } from "react-redux";
import { RootState } from "@store/index";
import { useNavigate } from "react-router-dom";
import { Edit2, Trash2 } from "lucide-react";
import { toast } from "react-toastify";

import usePermission from "@hooks/usePermission";

const RolesAdminPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const [page, setPage] = useState(1);
  const rolesState = useSelector((state: RootState) => state.roles);
  const roles = Array.isArray(rolesState.data)
    ? rolesState.data
    : rolesState.items || [];

  const fetchRoles = async () => {
    try {
      await Role.list();
    } catch (e) {
      console.error("Failed to fetch roles:", e);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, [page]);

  const handleDelete = async (id: number) => {
    if (window.confirm("Are you sure you want to delete this role?")) {
      try {
        await Role.remove(id);
        toast.success("Role deleted successfully");
        fetchRoles();
      } catch (error) {
        console.error("Failed to delete role:", error);
        toast.error("Failed to delete role");
      }
    }
  };

  const columns = [
    {
      title: "ID",
      key: "id",
      render: (item: any) => (
        <span style={{ fontWeight: 600, color: "#4a5568" }}>#{item.id}</span>
      ),
    },
    {
      title: "Role Name",
      key: "name",
      render: (item: any) => (
        <span style={{ fontWeight: 600, color: "#1e293b", fontSize: "14px" }}>
          {item.name}
        </span>
      ),
    },
    {
      title: "Permissions",
      key: "permissions",
      render: (item: any) => (
        <span style={{ color: "#718096", fontSize: "14px" }}>
          {item.permissions?.length || 0} modules access
        </span>
      ),
    },
    {
      title: "Created At",
      key: "createdAt",
      render: (item: any) => (
        <span style={{ color: "#718096", fontSize: "14px" }}>
          {new Date(item.createdAt || Date.now()).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          })}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (item: any) => (
        <div style={{ display: "flex", gap: "8px" }}>
          {hasPermission("roles", "Edit") && (
            <button
              onClick={() => navigate(`/admin/roles/edit/${item.id}`)}
              style={{
                padding: "6px",
                borderRadius: "6px",
                border: "1px solid #e2e8f0",
                background: "#ffffff",
                color: "#64748b",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
              onMouseOver={(e: any) => {
                e.currentTarget.style.color = "#f23236";
                e.currentTarget.style.borderColor = "#f23236";
              }}
              onMouseOut={(e: any) => {
                e.currentTarget.style.color = "#64748b";
                e.currentTarget.style.borderColor = "#e2e8f0";
              }}
            >
              <Edit2 size={16} />
            </button>
          )}
          {hasPermission("roles", "Delete") && (
            <button
              onClick={() => handleDelete(item.id)}
              style={{
                padding: "6px",
                borderRadius: "6px",
                border: "1px solid #e2e8f0",
                background: "#ffffff",
                color: "#64748b",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
              onMouseOver={(e: any) => {
                e.currentTarget.style.background = "#fff1f2";
                e.currentTarget.style.color = "#e11d48";
                e.currentTarget.style.borderColor = "#e11d48";
              }}
              onMouseOut={(e: any) => {
                e.currentTarget.style.background = "#ffffff";
                e.currentTarget.style.color = "#64748b";
                e.currentTarget.style.borderColor = "#e2e8f0";
              }}
            >
              <Trash2 size={16} />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <Column>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <h1
          style={{
            fontSize: "24px",
            fontWeight: 700,
            color: "#1a202c",
            margin: 0,
          }}
        >
          Roles Management
        </h1>
        {hasPermission("roles", "Add Role") && (
          <button
            onClick={() => navigate("/admin/roles/create")}
            style={{
              padding: "10px 24px",
              borderRadius: "8px",
              background: "#f23236",
              color: "#ffffff",
              border: "none",
              fontWeight: 600,
              cursor: "pointer",
              boxShadow: "0 4px 14px rgba(242, 50, 54, 0.39)",
              transition: "all 0.2s",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Add Role
          </button>
        )}
      </div>

      <div
        style={{
          background: "#ffffff",
          borderRadius: "16px",
          padding: "24px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.03)",
          border: "1px solid #f0f4f8",
        }}
      >
        <Table
          data={roles}
          columns={columns}
          totalItems={roles.length}
          totalPages={1}
          pageControlled={page}
          onPageChange={setPage}
          itemsPerPage={10}
          actions={[]}
        />
      </div>
    </Column>
  );
};

export default RolesAdminPage;
